import React, { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getChatThreads } from '../../Services/APIs/chat';
import { getCurrentCompanyId, logSocketEvent } from '../../Services/socket/chatSocket';
import { sampleThreads } from './mockData';
import { useSocket } from '../../Context/SocketContext';
import {
    buildThreadPreviewFromMessage,
    normalizeMessagePayload,
    USE_CHAT_MOCKS,
    formatDateOnly,
    formatDateTime,
    getDisplayName,
    getLastMessageBadge,
    getThreadEmail,
    normalizeThreadPayload,
    normalizeThreadsPayload,
    sortThreads,
    upsertThread
} from './helpers';

const ChatInboxPage = () => {
    const navigate = useNavigate();
    const { socket } = useSocket();
    const [threads, setThreads] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const filteredThreads = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();

        return sortThreads(
            threads.filter((thread) => {
                const matchesFilter = statusFilter === 'all' || thread?.status === statusFilter;
                if (!matchesFilter) return false;
                if (!normalizedSearch) return true;

                const haystack = [getDisplayName(thread), getThreadEmail(thread), thread?.subject || '']
                    .join(' ')
                    .toLowerCase();

                return haystack.includes(normalizedSearch);
            })
        );
    }, [threads, searchTerm, statusFilter]);

    useEffect(() => {
        const loadThreads = async () => {
            setLoading(true);
            setError('');

            try {
                if (USE_CHAT_MOCKS) {
                    setThreads(sortThreads(sampleThreads));
                    return;
                }

                const response = await getChatThreads({
                    company: getCurrentCompanyId(),
                    status: statusFilter === 'all' ? '' : statusFilter,
                    page: 1,
                    limit: 50
                });

                setThreads(sortThreads(normalizeThreadsPayload(response)));
            } catch (loadError) {
                console.error('Error loading chat threads:', loadError);
                setThreads([]);
                setError(loadError?.response?.data?.message || loadError?.message || 'Failed to load threads');
            } finally {
                setLoading(false);
            }
        };

        loadThreads();
    }, [statusFilter]);

    useEffect(() => {
        if (USE_CHAT_MOCKS || !socket) return undefined;

        const handleConnect = () => {
            logSocketEvent('inbox connected', socket.id);
        };
        const handleDisconnect = (reason) => {
            logSocketEvent('inbox disconnected', reason);
        };
        const handleThreadUpdated = (payload) => {
            logSocketEvent('inbox chatThreadUpdated', payload);

            const updatedThread = normalizeThreadPayload(payload);

            if (!updatedThread?.chatToken) return;

            setThreads((current) => sortThreads(upsertThread(current, updatedThread)));
        };
        const handleChatMessage = (payload) => {
            logSocketEvent('inbox chatMessage', payload);

            const message = normalizeMessagePayload(payload);
            const threadPreview = buildThreadPreviewFromMessage(message);

            if (!threadPreview?.chatToken) return;

            setThreads((current) => sortThreads(upsertThread(current, threadPreview)));
        };
        const handleSocketError = (socketError) => {
            logSocketEvent('inbox socketError', socketError);
            console.error('Socket error:', socketError);
        };

        socket.on('connect', handleConnect);
        socket.on('disconnect', handleDisconnect);
        socket.on('chatThreadUpdated', handleThreadUpdated);
        socket.on('chatMessage', handleChatMessage);
        socket.on('socketError', handleSocketError);

        if (socket.connected) {
            handleConnect();
        }

        return () => {
            socket.off('connect', handleConnect);
            socket.off('disconnect', handleDisconnect);
            socket.off('chatThreadUpdated', handleThreadUpdated);
            socket.off('chatMessage', handleChatMessage);
            socket.off('socketError', handleSocketError);
        };
    }, [socket]);

    const renderContent = () => {
        if (loading) {
            return (
                <div className="px-6 py-6">
                    <div className="space-y-4">
                        {Array.from({ length: 5 }).map((_, index) => (
                            <div key={index} className="animate-pulse rounded-lg border border-[#E8E6E3] px-4 py-4">
                                <div className="grid gap-4 lg:grid-cols-4">
                                    <div>
                                        <div className="h-4 w-32 rounded bg-[#EFEDE9]" />
                                        <div className="mt-2 h-3 w-40 rounded bg-[#F5F3F0]" />
                                    </div>
                                    <div className="h-4 w-full rounded bg-[#F5F3F0]" />
                                    <div className="h-4 w-28 rounded bg-[#F5F3F0]" />
                                    <div className="h-6 w-20 rounded-full bg-[#EFEDE9]" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="px-6 py-12 text-center">
                    <p className="text-sm text-[#B42318]">{error}</p>
                </div>
            );
        }

        if (filteredThreads.length === 0) {
            return (
                <div className="px-6 py-12 text-center">
                    <p className="text-base text-[#2D2D2D]">No threads found.</p>
                    <p className="mt-2 text-sm text-[#6B6B6B]">Try a different search or filter.</p>
                </div>
            );
        }

        return (
            <div className="space-y-3 p-4 md:p-5">
                {filteredThreads.map((thread) => {
                    const lastMessageBadge = getLastMessageBadge(thread);

                    return (
                        <button
                            key={thread.chatToken}
                            onClick={() =>
                                navigate(`/dashboard/messages/${thread.chatToken}`, {
                                    state: { thread }
                                })
                            }
                            className={`w-full rounded-2xl border px-5 py-4 text-left transition ${thread?.hasUnread
                                ? 'border-[#D4E7DA] bg-[#FBFDFC] shadow-[0_8px_24px_rgba(56,67,58,0.06)]'
                                : 'border-[#E8E6E3] bg-white hover:border-[#D8D4CF] hover:shadow-[0_8px_24px_rgba(34,43,37,0.06)]'
                                }`}
                        >
                            <div className="grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)_190px] lg:items-center">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#DBE9FF] to-[#EFF5FF] text-sm font-semibold text-[#34538A]">
                                            {(thread?.patient?.first_name || getDisplayName(thread) || 'U').charAt(0).toUpperCase()}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <p className={`truncate text-sm ${thread?.hasUnread ? 'font-semibold text-[#234033]' : 'font-semibold text-[#2D2D2D]'}`}>
                                                    {thread?.patient?.first_name || getDisplayName(thread)}
                                                </p>
                                                {thread?.hasUnread && <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#3C7A57]" />}
                                            </div>
                                            <p className="truncate text-xs font-medium text-[#6C7E92]">
                                                {thread?.customerEmail || getThreadEmail(thread)}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="min-w-0">
                                    <p className="truncate text-sm font-semibold text-[#2D2D2D]">
                                        {thread?.subject || 'No subject'}
                                    </p>
                                    <p className="mt-1 truncate text-sm text-[#6B6B6B]">
                                        {thread?.lastMessageText || 'No recent message'}
                                    </p>
                                </div>

                                <div className="flex flex-col items-start gap-2 lg:items-end">
                                    <p className="text-xs font-medium text-[#6B6B6B]">
                                        {formatDateTime(thread?.lastMessageAt)}
                                    </p>
                                    <span className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide ${lastMessageBadge.classes}`}>
                                        {lastMessageBadge.label}
                                    </span>
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>
        );
    };

    return (
        <main className="flex-1 overflow-auto bg-[#F7F7F8]">
            <div className="mx-auto max-w-6xl p-4 md:p-6 xl:p-8">
                <div className="rounded-2xl border border-[#E8E6E3] bg-white">
                    <div className="border-b border-[#E8E6E3] px-6 py-6">
                        <h1 className="text-3xl font-semibold tracking-tight text-[#2D2D2D]">Messages</h1>
                        <p className="mt-2 text-sm text-[#6B6B6B]">
                            View and manage patient conversations in a simple thread list.
                        </p>

                        <div className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                            <div className="relative w-full lg:max-w-md">
                                <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-[#9A958E]" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(event) => setSearchTerm(event.target.value)}
                                    placeholder="Search by patient, email, or subject"
                                    className="w-full rounded-xl border border-[#E8E6E3] bg-[#FCFBF9] py-3 pl-10 pr-4 text-sm text-[#2D2D2D] outline-none transition focus:border-[#38433A] focus:ring-2 focus:ring-[#DCE8DF]"
                                />
                            </div>

                        </div>
                    </div>

                    {renderContent()}

                    {!loading && !error && (
                        <div className="border-t border-[#E8E6E3] px-6 py-4 text-xs text-[#6B6B6B]">
                            {filteredThreads.length} thread{filteredThreads.length === 1 ? '' : 's'} shown
                            {threads.length > 0 && ` • latest started ${formatDateOnly(threads[0]?.createdAt)}`}
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
};

export default ChatInboxPage;
